/**
 * TourismExperienceContent.js
 *
 * Content section for tourism experience cards.
 * Displays experience-specific information like transport mode, stops, duration,
 * category, operator, and seasonal availability.
 */

import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../shared/Icon';
import { formatDistance as formatDistanceUtil } from '../../../utils/distanceFormat';
import { formatMonthDay } from '../../../utils/dateUtils';
import {
  VALID_EXPERIENCE_CATEGORIES, TRANSPORT_PRESENTATION, FOOT_CATEGORIES,
} from '../../../config/experienceCategories';
import useDistanceUnit from '../../../hooks/useDistanceUnit';
import { formatCurrencyRange } from '../../../utils/currencyFormat';

/**
 * Transport mode icon name mapping (maps to Icon component names).
 * Derived from the shared config so a new mode cannot render a blank pin here
 * while showing correctly elsewhere.
 */
const TRANSPORT_ICON_NAMES = Object.fromEntries(
  Object.entries(TRANSPORT_PRESENTATION).map(([mode, { icon }]) => [mode, icon])
);

/**
 * Format distance for display
 */
const formatCardDistance = (distance, unit) => {
  if (!distance) return null;
  if (typeof distance === 'number') {
    return formatDistanceUtil(distance, unit);
  }
  return distance;
};

/**
 * Difficulty badge (reused pattern from WalkingTourContent)
 */
const DifficultyBadge = ({ difficulty }) => {
  const { t } = useTranslation('feedCard');
  if (!difficulty) return null;

  const config = {
    easy: { labelKey: 'tourismExperienceContent.difficulty.easy', bgClass: 'bg-success-light', textClass: 'text-success' },
    moderate: { labelKey: 'tourismExperienceContent.difficulty.moderate', bgClass: 'bg-warning-light', textClass: 'text-warning-dark' },
    challenging: { labelKey: 'tourismExperienceContent.difficulty.challenging', bgClass: 'bg-warning-light', textClass: 'text-warning-dark' },
    difficult: { labelKey: 'tourismExperienceContent.difficulty.difficult', bgClass: 'bg-error-light', textClass: 'text-error' }
  };

  const { labelKey, bgClass, textClass } = config[difficulty] || config.moderate;

  return (
    <span className={`px-2 py-0.5 ${bgClass} ${textClass} text-xs rounded-full`}>
      {t(labelKey)}
    </span>
  );
};

// Experience category → catalog key stem (feedCard: tourismExperienceContent.categoryLabels.<stem>).
// Derived rather than hand-listed: every stem is the snake_case enum in
// camelCase, and keeping a parallel copy is how this list previously fell
// four categories behind the shared config.
const CATEGORY_LABEL_KEYS = Object.fromEntries(
  VALID_EXPERIENCE_CATEGORIES.map((key) => [
    key,
    key.replace(/_([a-z])/g, (_, c) => c.toUpperCase()),
  ])
);

/**
 * Format MM-DD season date for display, e.g. "05-15" → "May 15" (en) /
 * "15 mei" (nl). Delegates to the shared locale-aware helper — the old local
 * MONTH_NAMES array was English-only AND hardcoded month-before-day order.
 */
const formatSeasonDate = (mmdd) => formatMonthDay(mmdd);

/**
 * Check if a MM-DD date falls within a season range (handles year-wrap)
 */
const isDateInSeason = (mmdd, start, end) => {
  if (start <= end) {
    return mmdd >= start && mmdd <= end;
  }
  // Wraps around year boundary (e.g. Nov-Mar)
  return mmdd >= start || mmdd <= end;
};

/**
 * Compute days until a season opens from current MM-DD
 */
const daysUntilOpen = (currentMmdd, seasonStart) => {
  const now = new Date();
  const [sm, sd] = seasonStart.split('-').map(Number);
  let target = new Date(now.getFullYear(), sm - 1, sd);
  if (target <= now) target = new Date(now.getFullYear() + 1, sm - 1, sd);
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
};

/**
 * Season availability badge.
 *
 * Every variant leads with an icon, and the in-season one is primary-toned
 * rather than green. Both exist because this chip now shares a row with the
 * category and difficulty chips: category and DifficultyBadge's `easy` are both
 * bg-success-light/text-success, so a green "Open now" beside them read as one
 * wrapped chip, and the amber "Opens May 15" collided with `moderate` the same
 * way. Primary tone follows ActivityContent's context chip — this is about
 * TODAY, not a static attribute of the experience.
 */
const SeasonBadge = ({ yearRound, start, end }) => {
  const { t } = useTranslation('feedCard');
  if (yearRound || !start || !end) return null;

  const now = new Date();
  const currentMmdd = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const inSeason = isDateInSeason(currentMmdd, start, end);

  if (inSeason) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-lighter text-primary-dark text-xs rounded-full">
        <Icon name="clock" className="w-2.5 h-2.5" />
        {t('tourismExperienceContent.season.openNow')}
      </span>
    );
  }

  // Out of season — show when it opens
  const daysAway = daysUntilOpen(currentMmdd, start);
  const openDate = formatSeasonDate(start);
  const closesDate = formatSeasonDate(end);

  if (daysAway <= 60) {
    // Opens soon
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-warning-light text-warning-dark text-xs rounded-full">
        <Icon name="calendar" className="w-2.5 h-2.5" />
        {t('tourismExperienceContent.season.opensOn', { date: openDate })}
      </span>
    );
  }

  // Further out — show season range
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-background text-content-secondary text-xs rounded-full">
      <Icon name="calendar" className="w-2.5 h-2.5" />
      {t('tourismExperienceContent.season.dateRange', { start: openDate, end: closesDate })}
    </span>
  );
};

/**
 * Format price range
 */
const formatPriceRange = (min, max, currency) => formatCurrencyRange(min, max, currency);

/**
 * Main TourismExperienceContent Component
 */
const TourismExperienceContent = ({ data }) => {
  const { t } = useTranslation('feedCard');
  const distanceUnit = useDistanceUnit();

  /**
   * Format duration for display
   */
  const formatDuration = (duration) => {
    if (!duration) return null;
    if (typeof duration === 'number') {
      if (duration >= 60) {
        const hours = Math.floor(duration / 60);
        const mins = duration % 60;
        return mins > 0
          ? t('tourismExperienceContent.duration.hoursMinutes', { hours, mins })
          : t('tourismExperienceContent.duration.hoursOnly', { hours });
      }
      return t('tourismExperienceContent.duration.minutesOnly', { minutes: duration });
    }
    return duration;
  };

  /**
   * Experience category display name
   */
  const getCategoryLabel = (category) => {
    const keyStem = CATEGORY_LABEL_KEYS[category];
    if (keyStem) return t(`tourismExperienceContent.categoryLabels.${keyStem}`);
    return category ? category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null;
  };

  const { title, description, location, typeData } = data;
  const {
    stopCount = 0,
    duration,
    distance,
    difficulty,
    transportMode,
    experienceCategory,
    yearRound,
    operatingSeasonStart,
    operatingSeasonEnd,
    priceRangeMin,
    priceRangeMax,
    priceCurrency
  } = typeData || {};

  // FOOT_CATEGORIES replaces a local list that still named `glacier_hike` — a
  // category that does not exist — and omitted `park_circuit`, so Plitvice
  // rendered a boat icon for a boardwalk circuit.
  const transportIconName = FOOT_CATEGORIES.includes(experienceCategory)
    ? 'mountain'
    : (TRANSPORT_ICON_NAMES[transportMode] || 'map');
  const categoryLabel = getCategoryLabel(experienceCategory);
  const priceDisplay = formatPriceRange(priceRangeMin, priceRangeMax, priceCurrency);
  const showSeason = !yearRound && !!operatingSeasonStart && !!operatingSeasonEnd;

  return (
    <div className="flex-grow">
      {/* Title with transport icon */}
      <h3 className="font-semibold text-content-primary mb-2 line-clamp-1">
        <Icon name={transportIconName} className="w-4 h-4 inline mr-1" />{title}
      </h3>

      {/* Stats row */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-content-secondary mb-2">
        {!!stopCount && (
          <span className="flex items-center">
            <Icon name="mapPin" className="w-3.5 h-3.5 mr-1" />
            {t('tourismExperienceContent.stopCount', { count: stopCount })}
          </span>
        )}
        {duration && (
          <span className="flex items-center">
            <Icon name="stopwatch" className="w-3.5 h-3.5 mr-1" />
            {formatDuration(duration)}
          </span>
        )}
        {distance && (
          <span className="flex items-center">
            <Icon name="ruler" className="w-3.5 h-3.5 mr-1" />
            {formatCardDistance(distance, distanceUnit)}
          </span>
        )}
      </div>

      {/* Category + difficulty + season badges. The season chip shares this row
          instead of owning one: a row to itself made this the tallest card on
          Home, which stretches every other card in the strip to match.
          SeasonBadge carries an icon and its own colour so it stays legible as
          a DIFFERENT kind of chip beside the attribute chips it now sits with. */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {categoryLabel && (
          <span className="px-2 py-0.5 bg-success-light text-success text-xs rounded-full">
            {categoryLabel}
          </span>
        )}
        <DifficultyBadge difficulty={difficulty} />
        {showSeason && (
          <SeasonBadge yearRound={yearRound} start={operatingSeasonStart} end={operatingSeasonEnd} />
        )}
      </div>

      {/* Location + price share a row (the operator name they used to sit with
          belongs to the detail page). The price is a NON-SHRINKING sibling of
          the clamped location, not text inside it: locations here run long
          ("Aggtelek National Park, Borsod-Abaúj-Zemplén County, Northern
          Hungary") and line-clamp-1 clamps at the END, so an inline price was
          cut off on most cards — and a cut range reads as a real number
          ("CHF 72–CHF …"). Same shape as EventContent's venue + follow chip. */}
      {(location || priceDisplay) && (
        <div className="flex items-center gap-1.5 mb-2">
          {location && (
            <p className="text-sm text-content-secondary line-clamp-1 flex-1 min-w-0">
              <Icon name="mapPin" className="w-3.5 h-3.5 inline mr-1" />{location}
            </p>
          )}
          {priceDisplay && (
            <span className="text-sm font-medium text-content-secondary flex-shrink-0">{priceDisplay}</span>
          )}
        </div>
      )}

      {/* Description */}
      {description && (
        <p className="text-sm text-content-secondary mb-3 line-clamp-2">
          {description}
        </p>
      )}
    </div>
  );
};

export default memo(TourismExperienceContent);
